import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { PlayerSubNav } from "@/components/common/PlayerSubNav";
import {
  Target, Trophy, Star, Crown, Building2, Layers, Eye, EyeOff, ShieldAlert,
  Search, Filter, ArrowUpDown, Pencil, ChevronRight, Briefcase, GraduationCap,
  Zap, Heart, Flame, Activity, Calendar as CalendarIcon, ExternalLink, Lock,
  Wand2, Check, X, Sparkles, FileDown, Scale,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const SUB_SCORES = [
  { key: "scheme_fit_score", label: "Scheme Fit", icon: Target, color: "#22D3EE" },
  { key: "position_need_score", label: "Position Need", icon: Briefcase, color: "#34D399" },
  { key: "draft_range_score", label: "Draft Range", icon: Layers, color: "#A78BFA" },
  { key: "roster_opportunity_score", label: "Roster Opportunity", icon: Crown, color: "#FBBF24" },
  { key: "coaching_dev_score", label: "Coaching / Dev", icon: GraduationCap, color: "#60A5FA" },
  { key: "special_teams_score", label: "Special Teams", icon: Zap, color: "#F472B6" },
  { key: "archetype_match_score", label: "Archetype Match", icon: Activity, color: "#22D3EE" },
  { key: "cultural_fit_score", label: "Cultural Fit", icon: Heart, color: "#FB7185" },
];

const TIER_META = {
  priority:    { label: "Priority Fits",    short: "T1", tone: "emerald", description: "Strong scheme + roster + draft alignment.", color: "#34D399" },
  strong:      { label: "Strong Fits",      short: "T2", tone: "cyan",    description: "Good fit with one moderate concern.",       color: "#22D3EE" },
  development: { label: "Development Fits", short: "T3", tone: "ice",     description: "Could develop here; opportunity is limited.", color: "#A5B4FC" },
  watch_list:  { label: "Watch List",       short: "T4", tone: "amber",   description: "Need may change — monitor.",                  color: "#FBBF24" },
  poor:        { label: "Poor Fit",         short: "T5", tone: "rose",    description: "Low scheme or opportunity alignment.",        color: "#FB7185" },
};

const BADGE_META = {
  elite_fit:          { label: "Elite Fit",         tone: "emerald", icon: Crown },
  strong_fit:         { label: "Strong Fit",        tone: "cyan",    icon: Star },
  development_fit:    { label: "Development Fit",   tone: "ice",     icon: GraduationCap },
  special_teams_path: { label: "Special Teams Path", tone: "amber",  icon: Zap },
  watch_list:         { label: "Watch List",        tone: "amber",   icon: Eye },
  poor_fit:           { label: "Poor Fit",          tone: "rose",    icon: Flame },
};

const RISK_META = {
  low:    { label: "Low risk",    tone: "ice" },
  medium: { label: "Medium risk", tone: "amber" },
  high:   { label: "High risk",   tone: "rose" },
};

const MEETING_STATUS = {
  no_contact:        { label: "No contact",        tone: "neutral" },
  informal_meeting:  { label: "Informal meeting",  tone: "ice" },
  formal_meeting:    { label: "Formal meeting",    tone: "cyan" },
  pro_day_contact:   { label: "Pro Day contact",   tone: "cyan" },
  private_workout:   { label: "Private workout",   tone: "emerald" },
  follow_up:         { label: "Follow-up",         tone: "amber" },
  high_interest:     { label: "High interest",     tone: "emerald" },
};

const WORKOUT_STATUS = {
  none:      { label: "—",          tone: "neutral" },
  scheduled: { label: "Scheduled",  tone: "cyan" },
  completed: { label: "Completed",  tone: "emerald" },
  cancelled: { label: "Cancelled",  tone: "rose" },
};

const FILTERS = [
  { key: "all",                       label: "All evaluated" },
  { key: "best_overall",              label: "Best overall fit" },
  { key: "best_scheme",               label: "Best scheme fit" },
  { key: "highest_need",              label: "Highest roster need" },
  { key: "best_special_teams",        label: "Best special teams pathway" },
  { key: "best_development",          label: "Best development environment" },
  { key: "lowest_risk",               label: "Lowest risk" },
  { key: "watch_list_only",           label: "Watch list only" },
  { key: "meeting_completed",         label: "Meeting completed" },
  { key: "private_workout_scheduled", label: "Private workout scheduled" },
];

const SORT_OPTIONS = [
  { key: "fit_score",                value: "Overall fit score" },
  { key: "scheme_fit_score",         value: "Scheme fit" },
  { key: "roster_opportunity_score", value: "Roster opportunity" },
  { key: "special_teams_score",      value: "Special teams pathway" },
  { key: "risk_level",               value: "Risk level" },
  { key: "draft_range_score",        value: "Draft range alignment" },
  { key: "updated_at",               value: "Last updated" },
];

function useResolvedPlayerId() {
  const { user } = useAuth();
  const { playerId } = useParams();
  return playerId || user?.player_id;
}

function tinyBar(score, color = "#22D3EE") {
  const s = Math.max(0, Math.min(100, score || 0));
  return (
    <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${s}%`, background: color }} />
    </div>
  );
}

// ============================================================
// Overview hero
// ============================================================
function OverviewSummary({ summary }) {
  if (!summary) return null;
  const blocks = [
    { key: "top_overall",            label: "Top Overall Fit",          icon: Trophy,    data: summary.top_overall },
    { key: "best_scheme",            label: "Best Scheme Fit",          icon: Target,    data: summary.best_scheme },
    { key: "best_roster_opportunity",label: "Best Roster Opportunity",  icon: Crown,     data: summary.best_roster_opportunity },
    { key: "best_special_teams",     label: "Best Special Teams Path",  icon: Zap,       data: summary.best_special_teams },
    { key: "best_development",       label: "Best Development Env.",    icon: GraduationCap, data: summary.best_development },
    { key: "highest_risk",           label: "Highest Risk Fit",         icon: ShieldAlert, data: summary.highest_risk },
  ];
  return (
    <GlassCard className="col-span-12 p-6 relative overflow-hidden" testId="tf-overview">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.18), transparent 70%)" }} />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-1 font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-cyan-300" /> Team Fit Overview</h3>
            <div className="text-3 text-xs mt-0.5">Front-office decision board — best fits across scheme, roster, and pathway dimensions.</div>
          </div>
          <StatusPill tone="ice">
            {summary.total_evaluated} of {summary.total_teams} teams evaluated
          </StatusPill>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {blocks.map(({ key, label, icon: Icon, data }) => (
            <div key={key} className="glass p-3 ring-1 ring-white/5" data-testid={`tf-overview-${key}`}>
              <div className="flex items-center gap-1.5 text-3 text-[10px] uppercase tracking-wider">
                <Icon className="h-3 w-3" /> {label}
              </div>
              {data ? (
                <>
                  <div className="text-1 font-semibold mt-1.5 leading-tight">{data.team_name}</div>
                  <div className="text-3 text-[11px] mt-0.5 font-mono">
                    {data.team_abbreviation}{data.score ? ` · ${data.score}` : ""}{data.risk_level ? ` · ${data.risk_level} risk` : ""}
                  </div>
                </>
              ) : (
                <div className="text-3 text-sm italic mt-2">Not yet rated</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </GlassCard>
  );
}

// ============================================================
// Team card
// ============================================================
function TeamFitCard({ fit, isAdmin, onOpen, onEdit, compareMode, compareSelected, onToggleCompare }) {
  const tierMeta = TIER_META[fit.tier] || TIER_META.poor;
  const meetingMeta = MEETING_STATUS[fit.meeting_status] || MEETING_STATUS.no_contact;
  const riskMeta = RISK_META[fit.risk_level] || RISK_META.medium;
  const overall = fit.fit_score || 0;
  const isRated = overall > 0 || fit.scheme_fit_score > 0;

  const handleClick = () => {
    if (compareMode) onToggleCompare(fit);
    else onOpen(fit);
  };

  return (
    <div
      className={`glass p-5 relative overflow-hidden cursor-pointer transition ${
        compareSelected ? "ring-2 ring-violet-400/80 shadow-[0_0_24px_rgba(167,139,250,0.25)]" : "hover:ring-1 hover:ring-cyan-300/30"
      } ${!isRated ? "opacity-60" : ""}`}
      onClick={handleClick}
      data-testid={`tf-card-${fit.team_abbreviation}`}
      data-compare-selected={compareSelected ? "true" : "false"}
    >
      <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full pointer-events-none" style={{ background: `radial-gradient(closest-side, ${tierMeta.color}30, transparent 70%)` }} />

      {compareMode && (
        <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={compareSelected}
            onCheckedChange={() => onToggleCompare(fit)}
            className="border-violet-300/60 data-[state=checked]:bg-violet-400 data-[state=checked]:text-[#04111A]"
            data-testid={`tf-compare-check-${fit.team_abbreviation}`}
          />
        </div>
      )}
      {/* header */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-11 w-11 rounded-lg bg-white/5 ring-1 ring-white/10 grid place-items-center text-1 font-mono font-bold text-sm shrink-0">
            {fit.team_abbreviation}
          </div>
          <div className="min-w-0">
            <div className="text-1 font-semibold truncate leading-tight">{fit.team_name}</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <StatusPill tone={tierMeta.tone}>{tierMeta.short} · {tierMeta.label}</StatusPill>
              {fit.admin_marked_high_interest && <StatusPill tone="emerald">High interest</StatusPill>}
              {!fit.visible_to_family && isAdmin && (
                <StatusPill tone="neutral"><Lock className="h-3 w-3" /> Internal</StatusPill>
              )}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-1 text-3xl font-semibold font-mono leading-none">{overall || "—"}</div>
          <div className="text-3 text-[10px] mt-0.5">/ 100</div>
        </div>
      </div>

      {/* badges */}
      {(fit.badges || []).length > 0 && (
        <div className="relative mt-3 flex items-center gap-1.5 flex-wrap">
          {fit.badges.map((b) => {
            const m = BADGE_META[b];
            if (!m) return null;
            const Icon = m.icon;
            return (
              <StatusPill key={b} tone={m.tone}>
                <Icon className="h-3 w-3" /> {m.label}
              </StatusPill>
            );
          })}
        </div>
      )}

      {/* sub-scores */}
      <div className="relative mt-4 grid grid-cols-2 gap-2">
        {SUB_SCORES.slice(0, 6).map((s) => (
          <div key={s.key}>
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-3">
              <span className="truncate">{s.label}</span>
              <span className="text-1 font-mono">{fit[s.key] || 0}</span>
            </div>
            <div className="mt-1">{tinyBar(fit[s.key] || 0, s.color)}</div>
          </div>
        ))}
      </div>

      {/* footer meta */}
      <div className="relative mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatusPill tone={meetingMeta.tone}>{meetingMeta.label}</StatusPill>
          <StatusPill tone={riskMeta.tone}>{riskMeta.label}</StatusPill>
        </div>
        <div className="flex items-center gap-2">
          {fit.draft_range_estimate && (
            <span className="text-3 text-[11px] flex items-center gap-1">
              <Layers className="h-3 w-3" /> {fit.draft_range_estimate}
            </span>
          )}
          {isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(fit); }}
              className="text-2 hover:text-white p-1"
              data-testid={`tf-edit-${fit.team_abbreviation}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          <ChevronRight className="h-3.5 w-3.5 text-3" />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Team detail drawer
// ============================================================
function TeamFitDrawer({ open, onOpenChange, fit, isAdmin, positionFields, onEdit, onAIGenerate, onAIApprove, onAIReject, aiBusy }) {
  if (!fit) return null;
  const tierMeta = TIER_META[fit.tier] || TIER_META.poor;
  const draft = fit.ai_fit_explanation_draft;
  const hasApproved = !!fit.ai_fit_explanation_md && fit.ai_approved;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-3xl text-1 max-h-[85vh] overflow-y-auto" data-testid="tf-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-white/5 ring-1 ring-white/10 grid place-items-center font-mono font-bold text-sm">{fit.team_abbreviation}</div>
            <div>
              <div className="text-1 font-semibold">{fit.team_name}</div>
              <div className="text-3 text-[11px] flex items-center gap-2 mt-0.5">
                <StatusPill tone={tierMeta.tone}>{tierMeta.short} · {tierMeta.label}</StatusPill>
                {fit.admin_marked_high_interest && <StatusPill tone="emerald">High interest</StatusPill>}
                {hasApproved && <StatusPill tone="violet">AI Published</StatusPill>}
                {draft && !hasApproved && <StatusPill tone="amber">AI Draft — pending approval</StatusPill>}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-4 mt-2">
          {/* sub-score breakdown */}
          <div className="col-span-12 md:col-span-7">
            <div className="text-3 text-[10px] uppercase tracking-wider mb-2">Score Breakdown</div>
            <div className="grid grid-cols-2 gap-3">
              {SUB_SCORES.map((s) => (
                <div key={s.key} className="glass p-2.5">
                  <div className="flex items-center justify-between text-[11px] text-3">
                    <span className="flex items-center gap-1.5"><s.icon className="h-3 w-3" style={{ color: s.color }} /> {s.label}</span>
                    <span className="text-1 font-mono">{fit[s.key] || 0}</span>
                  </div>
                  <div className="mt-1.5">{tinyBar(fit[s.key] || 0, s.color)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* status block */}
          <div className="col-span-12 md:col-span-5 space-y-2">
            <div className="glass p-3">
              <div className="text-3 text-[10px] uppercase tracking-wider">Overall fit</div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="text-1 text-3xl font-semibold font-mono">{fit.fit_score || 0}</div>
                <div className="text-3 text-xs">/ 100</div>
                <StatusPill tone={(RISK_META[fit.risk_level] || RISK_META.medium).tone}>{(RISK_META[fit.risk_level] || RISK_META.medium).label}</StatusPill>
              </div>
            </div>
            <DrawerLine label="Meeting status" value={(MEETING_STATUS[fit.meeting_status] || MEETING_STATUS.no_contact).label} />
            <DrawerLine label="Workout status" value={(WORKOUT_STATUS[fit.workout_status] || WORKOUT_STATUS.none).label} />
            <DrawerLine label="Draft range" value={fit.draft_range_estimate || "—"} />
          </div>

          {/* narrative blocks */}
          <DrawerNote className="col-span-12" label="Why this team fits" value={fit.scheme_reason} accent />
          <DrawerNote className="col-span-12" label="Why this team may not fit" value={fit.needs_reason} />
          <DrawerNote className="col-span-12 md:col-span-6" label="Scheme notes" value={fit.scheme_notes} />
          <DrawerNote className="col-span-12 md:col-span-6" label="Special teams pathway" value={fit.special_teams_pathway} />

          {/* internal-only */}
          {isAdmin && (
            <>
              <DrawerNote className="col-span-12 md:col-span-6" label="Depth chart notes" value={fit.depth_chart_notes} icon={Lock} />
              <DrawerNote className="col-span-12 md:col-span-6" label="Advisor note" value={fit.advisor_note} icon={Lock} accent />
              <DrawerNote className="col-span-12 md:col-span-6" label="Risk notes" value={fit.risk_notes} icon={Lock} />
              <DrawerNote className="col-span-12 md:col-span-6" label="Contact notes" value={fit.contact_notes} icon={Lock} />
            </>
          )}

          {/* position-specific */}
          {positionFields.length > 0 && (
            <div className="col-span-12">
              <div className="text-3 text-[10px] uppercase tracking-wider mb-2">Position-Specific Fit</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {positionFields.map((pf) => {
                  const v = (fit.position_fit || {})[pf.key] || {};
                  return (
                    <div key={pf.key} className="glass p-2.5" data-testid={`tf-posfit-${pf.key}`}>
                      <div className="flex items-center justify-between text-[11px] text-3">
                        <span>{pf.label}</span>
                        <span className="text-1 font-mono">{v.score || 0}</span>
                      </div>
                      <div className="mt-1.5">{tinyBar(v.score || 0, "#A78BFA")}</div>
                      {v.note && <div className="text-2 text-[12px] mt-1.5 leading-relaxed">{v.note}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Approved AI explanation (visible to non-admin only when approved) */}
          {fit.ai_fit_explanation_md && fit.ai_approved && (
            <div className="col-span-12 glass p-4 ring-1 ring-violet-300/25" data-testid="tf-ai-approved">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-wider text-violet-200 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> AI Fit Explanation — Published
                </div>
                {fit.ai_approved_at && (
                  <div className="text-3 text-[10px]">Approved {new Date(fit.ai_approved_at).toLocaleString()}</div>
                )}
              </div>
              <div className="markdown-body markdown-compact text-2 text-[13px] mt-2 leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{fit.ai_fit_explanation_md}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* AI draft review (admin only, when draft exists and not yet approved) */}
          {isAdmin && draft && !hasApproved && (
            <div className="col-span-12 glass p-4 ring-1 ring-amber-300/30 relative overflow-hidden" data-testid="tf-ai-draft">
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(167,139,250,0.25), transparent 70%)" }} />
              <div className="relative">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-[10px] uppercase tracking-wider text-amber-200 flex items-center gap-1.5">
                    <Wand2 className="h-3 w-3" /> AI Draft — Awaiting approval
                  </div>
                  {draft.generated_at && (
                    <div className="text-3 text-[10px]">Generated {new Date(draft.generated_at).toLocaleString()}</div>
                  )}
                </div>
                {draft.explanation_md && (
                  <div className="markdown-body markdown-compact text-2 text-[13px] mt-2 leading-relaxed">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.explanation_md}</ReactMarkdown>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {(draft.strengths || []).length > 0 && (
                    <div className="glass p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-200">Strengths</div>
                      <ul className="mt-1 space-y-1 text-2 text-[12.5px] list-disc list-inside">
                        {draft.strengths.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  {(draft.concerns || []).length > 0 && (
                    <div className="glass p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-rose-200">Concerns</div>
                      <ul className="mt-1 space-y-1 text-2 text-[12.5px] list-disc list-inside">
                        {draft.concerns.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
                {draft.family_friendly_summary_md && (
                  <div className="glass p-2.5 mt-3">
                    <div className="text-[10px] uppercase tracking-wider text-cyan-200">Family-Friendly Summary</div>
                    <div className="markdown-body text-2 text-[12.5px] mt-1 leading-relaxed">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.family_friendly_summary_md}</ReactMarkdown>
                    </div>
                  </div>
                )}
                <div className="text-3 text-[10px] italic mt-2">
                  Approving publishes the explanation_md field to the team fit. Family viewers will see it only after approval and if this fit is marked visible to family.
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {isAdmin && !draft && !hasApproved && (
            <Button
              data-testid="tf-ai-generate"
              onClick={() => onAIGenerate(fit)}
              disabled={aiBusy}
              variant="ghost"
              className="text-violet-200 hover:text-violet-100 hover:bg-violet-300/10"
            >
              <Wand2 className="h-4 w-4" /> {aiBusy ? "Generating…" : "Generate AI Explanation"}
            </Button>
          )}
          {isAdmin && draft && !hasApproved && (
            <>
              <Button
                data-testid="tf-ai-reject"
                onClick={() => onAIReject(fit)}
                disabled={aiBusy}
                variant="ghost"
                className="text-rose-200 hover:text-rose-100 hover:bg-rose-300/10"
              >
                <X className="h-4 w-4" /> Discard draft
              </Button>
              <Button
                data-testid="tf-ai-regenerate"
                onClick={() => onAIGenerate(fit)}
                disabled={aiBusy}
                variant="ghost"
                className="text-2"
              >
                <Wand2 className="h-4 w-4" /> Regenerate
              </Button>
              <Button
                data-testid="tf-ai-approve"
                onClick={() => onAIApprove(fit)}
                disabled={aiBusy}
                className="cta-primary"
              >
                <Check className="h-4 w-4" /> Approve & publish
              </Button>
            </>
          )}
          {isAdmin && hasApproved && (
            <Button
              data-testid="tf-ai-regenerate-approved"
              onClick={() => onAIGenerate(fit)}
              disabled={aiBusy}
              variant="ghost"
              className="text-2"
            >
              <Wand2 className="h-4 w-4" /> Regenerate (will create new draft)
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Close</Button>
          {isAdmin && (
            <Button data-testid="tf-drawer-edit" onClick={() => { onEdit(fit); onOpenChange(false); }} className="cta-primary">
              <Pencil className="h-4 w-4" /> Edit team fit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DrawerLine({ label, value }) {
  return (
    <div className="glass p-3 flex items-center justify-between gap-2">
      <div className="text-3 text-[11px] uppercase tracking-wider">{label}</div>
      <div className="text-1 text-sm font-medium">{value}</div>
    </div>
  );
}

function DrawerNote({ label, value, icon: Icon, className = "", accent = false }) {
  if (!value) return null;
  return (
    <div className={`${className} ${accent ? "glass p-3" : "p-1"}`}>
      <div className={`text-[10px] uppercase tracking-wider flex items-center gap-1.5 ${accent ? "text-cyan-200" : "text-3"}`}>
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-2 text-[13px] leading-relaxed mt-1 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

// ============================================================
// Edit dialog (admin)
// ============================================================
function TeamFitEditor({ open, onOpenChange, fit, positionFields, onSave }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open && fit) {
      setForm({
        ...fit,
        position_fit: { ...(fit.position_fit || {}) },
      });
    }
  }, [open, fit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updatePosFit = (key, patch) => setForm((f) => ({
    ...f,
    position_fit: { ...(f.position_fit || {}), [key]: { ...((f.position_fit || {})[key] || { score: 0, note: "" }), ...patch } },
  }));

  const submit = () => {
    const payload = {
      ...SUB_SCORES.reduce((acc, s) => { acc[s.key] = Number(form[s.key] || 0); return acc; }, {}),
      risk_level: form.risk_level,
      meeting_status: form.meeting_status,
      workout_status: form.workout_status,
      draft_range_estimate: form.draft_range_estimate || null,
      scheme_reason: form.scheme_reason || "",
      needs_reason: form.needs_reason || "",
      scheme_notes: form.scheme_notes || null,
      depth_chart_notes: form.depth_chart_notes || null,
      special_teams_pathway: form.special_teams_pathway || null,
      advisor_note: form.advisor_note || null,
      risk_notes: form.risk_notes || null,
      contact_notes: form.contact_notes || null,
      watch_list: !!form.watch_list,
      admin_marked_high_interest: !!form.admin_marked_high_interest,
      visible_to_family: !!form.visible_to_family,
      fit_score: Number(form.fit_score || 0),
      fit_score_overridden: !!form.fit_score_overridden,
      position_fit: form.position_fit || {},
    };
    onSave(payload);
    onOpenChange(false);
  };

  if (!fit) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-4xl text-1 max-h-[85vh] overflow-y-auto" data-testid="tf-editor">
        <DialogHeader><DialogTitle>Edit Team Fit · {fit.team_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-12 gap-3">
          {/* Sub-scores */}
          <div className="col-span-12">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Sub-Scores (0–100)</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1.5">
              {SUB_SCORES.map((s) => (
                <div key={s.key}>
                  <div className="text-3 text-[11px]">{s.label}</div>
                  <Input
                    data-testid={`tf-edit-${s.key}`}
                    type="number" min="0" max="100"
                    value={form[s.key] ?? 0}
                    onChange={(e) => update(s.key, e.target.value)}
                    className="bg-white/5 border-white/10 text-1 font-mono mt-1"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Overall override */}
          <div className="col-span-12 md:col-span-4 glass p-3">
            <div className="flex items-center justify-between">
              <Label className="text-3 text-[10px] uppercase tracking-wider">Override overall</Label>
              <Switch checked={!!form.fit_score_overridden} onCheckedChange={(v) => update("fit_score_overridden", v)} />
            </div>
            <Input type="number" min="0" max="100" disabled={!form.fit_score_overridden}
              value={form.fit_score ?? 0} onChange={(e) => update("fit_score", e.target.value)}
              className="bg-white/5 border-white/10 text-1 font-mono mt-2" data-testid="tf-edit-fit-score" />
            <div className="text-3 text-[10px] mt-1">If off, overall is auto-computed from sub-scores.</div>
          </div>

          {/* Risk + status */}
          <Field label="Risk level" colSpan={4} testId="tf-edit-risk">
            <Select value={form.risk_level} onValueChange={(v) => update("risk_level", v)}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Draft range estimate" colSpan={4} testId="tf-edit-draft-range">
            <Input value={form.draft_range_estimate || ""} onChange={(e) => update("draft_range_estimate", e.target.value)} className="bg-white/5 border-white/10 text-1" placeholder="e.g., Round 2 (38–55)" />
          </Field>
          <Field label="Meeting status" colSpan={4} testId="tf-edit-meeting">
            <Select value={form.meeting_status} onValueChange={(v) => update("meeting_status", v)}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {Object.entries(MEETING_STATUS).map(([k, v]) => (<SelectItem key={k} value={k}>{v.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Workout status" colSpan={4} testId="tf-edit-workout">
            <Select value={form.workout_status} onValueChange={(v) => update("workout_status", v)}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {Object.entries(WORKOUT_STATUS).map(([k, v]) => (<SelectItem key={k} value={k}>{v.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>

          {/* Visibility flags */}
          <div className="col-span-12 md:col-span-8 grid grid-cols-3 gap-2">
            <ToggleField label="Watch list" testId="tf-edit-watch" checked={!!form.watch_list} onChange={(v) => update("watch_list", v)} />
            <ToggleField label="High interest" testId="tf-edit-high-interest" checked={!!form.admin_marked_high_interest} onChange={(v) => update("admin_marked_high_interest", v)} />
            <ToggleField label="Visible to player/family" testId="tf-edit-visible" checked={!!form.visible_to_family} onChange={(v) => update("visible_to_family", v)} />
          </div>

          {/* Narrative */}
          <Field label="Why this team fits" colSpan={6} testId="tf-edit-fit"><Textarea rows={2} value={form.scheme_reason || ""} onChange={(e) => update("scheme_reason", e.target.value)} className="bg-white/5 border-white/10 text-1" /></Field>
          <Field label="Why this team may not fit" colSpan={6} testId="tf-edit-not-fit"><Textarea rows={2} value={form.needs_reason || ""} onChange={(e) => update("needs_reason", e.target.value)} className="bg-white/5 border-white/10 text-1" /></Field>
          <Field label="Scheme notes" colSpan={6} testId="tf-edit-scheme"><Textarea rows={2} value={form.scheme_notes || ""} onChange={(e) => update("scheme_notes", e.target.value)} className="bg-white/5 border-white/10 text-1" /></Field>
          <Field label="Special teams pathway" colSpan={6} testId="tf-edit-st"><Textarea rows={2} value={form.special_teams_pathway || ""} onChange={(e) => update("special_teams_pathway", e.target.value)} className="bg-white/5 border-white/10 text-1" /></Field>
          <Field label="Depth chart notes (internal)" colSpan={6} testId="tf-edit-depth"><Textarea rows={2} value={form.depth_chart_notes || ""} onChange={(e) => update("depth_chart_notes", e.target.value)} className="bg-white/5 border-white/10 text-1" /></Field>
          <Field label="Advisor note (internal)" colSpan={6} testId="tf-edit-advisor"><Textarea rows={2} value={form.advisor_note || ""} onChange={(e) => update("advisor_note", e.target.value)} className="bg-white/5 border-white/10 text-1" /></Field>
          <Field label="Risk notes (internal)" colSpan={6} testId="tf-edit-risk-notes"><Textarea rows={2} value={form.risk_notes || ""} onChange={(e) => update("risk_notes", e.target.value)} className="bg-white/5 border-white/10 text-1" /></Field>
          <Field label="Contact notes (internal)" colSpan={6} testId="tf-edit-contact"><Textarea rows={2} value={form.contact_notes || ""} onChange={(e) => update("contact_notes", e.target.value)} className="bg-white/5 border-white/10 text-1" /></Field>

          {/* Position-specific fit */}
          {positionFields.length > 0 && (
            <div className="col-span-12">
              <Label className="text-3 text-[10px] uppercase tracking-wider">Position-Specific Fit</Label>
              <div className="space-y-2 mt-1.5">
                {positionFields.map((pf) => {
                  const v = (form.position_fit || {})[pf.key] || { score: 0, note: "" };
                  return (
                    <div key={pf.key} className="grid grid-cols-12 gap-2 items-start glass p-2" data-testid={`tf-edit-posfit-${pf.key}`}>
                      <div className="col-span-3 text-2 text-[12px] pt-1.5">{pf.label}</div>
                      <Input className="col-span-2 bg-white/5 border-white/10 text-1 font-mono" type="number" min="0" max="100" value={v.score || 0} onChange={(e) => updatePosFit(pf.key, { score: Number(e.target.value) })} />
                      <Input className="col-span-7 bg-white/5 border-white/10 text-1" placeholder="Note (optional)" value={v.note || ""} onChange={(e) => updatePosFit(pf.key, { note: e.target.value })} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="tf-editor-save" onClick={submit} className="cta-primary">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, colSpan = 12, testId }) {
  return (
    <div className={`col-span-${colSpan}`} data-testid={testId}>
      <Label className="text-3 text-[10px] uppercase tracking-wider">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ToggleField({ label, checked, onChange, testId }) {
  return (
    <div className="glass p-2.5 flex items-center justify-between gap-2" data-testid={testId}>
      <Label className="text-2 text-[12px]">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

// ============================================================
// Page
// ============================================================
export default function TeamFitsPage() {
  const { user } = useAuth();
  const playerId = useResolvedPlayerId();
  const isAdmin = user?.role === "admin";
  const [player, setPlayer] = useState(null);
  const [fits, setFits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [positionFields, setPositionFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sortKey, setSortKey] = useState("fit_score");
  const [search, setSearch] = useState("");
  const [openFit, setOpenFit] = useState(null);
  const [editor, setEditor] = useState({ open: false, fit: null });

  const load = async () => {
    if (!playerId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [p, fl, sm] = await Promise.all([
        api.get(`/players/${playerId}`),
        api.get("/team-fits", { params: { player_id: playerId } }),
        api.get("/team-fits/summary", { params: { player_id: playerId } }).catch(() => ({ data: null })),
      ]);
      setPlayer(p.data);
      setFits(fl.data || []);
      setSummary(sm.data);
      const cfg = await api.get("/team-fits/config/positions", { params: { position: p.data.position } }).catch(() => ({ data: { fields: [] } }));
      setPositionFields(cfg.data.fields || []);
    } catch (e) {
      toast.error("Failed to load team fits");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [playerId]);

  // Filter + sort
  const visibleFits = useMemo(() => {
    let arr = [...fits];
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((f) => f.team_name.toLowerCase().includes(q) || f.team_abbreviation.toLowerCase().includes(q));
    }
    switch (filter) {
      case "best_overall":               arr = arr.filter((f) => (f.fit_score || 0) >= 70); break;
      case "best_scheme":                arr = arr.filter((f) => (f.scheme_fit_score || 0) >= 70); break;
      case "highest_need":               arr = arr.filter((f) => (f.position_need_score || 0) >= 70); break;
      case "best_special_teams":         arr = arr.filter((f) => (f.special_teams_score || 0) >= 50); break;
      case "best_development":           arr = arr.filter((f) => (f.coaching_dev_score || 0) >= 70); break;
      case "lowest_risk":                arr = arr.filter((f) => f.risk_level === "low" && (f.fit_score || 0) > 0); break;
      case "watch_list_only":            arr = arr.filter((f) => f.watch_list); break;
      case "meeting_completed":          arr = arr.filter((f) => ["formal_meeting", "private_workout", "follow_up", "high_interest"].includes(f.meeting_status)); break;
      case "private_workout_scheduled":  arr = arr.filter((f) => f.workout_status === "scheduled" || f.meeting_status === "private_workout"); break;
      case "all":
      default: arr = arr.filter((f) => (f.fit_score || 0) > 0 || (f.scheme_fit_score || 0) > 0); break;
    }
    if (sortKey === "risk_level") {
      const order = { high: 0, medium: 1, low: 2 };
      arr.sort((a, b) => (order[a.risk_level] ?? 9) - (order[b.risk_level] ?? 9));
    } else if (sortKey === "updated_at") {
      arr.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    } else {
      arr.sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
    }
    return arr;
  }, [fits, filter, sortKey, search]);

  // Group by tier
  const byTier = useMemo(() => {
    const groups = { priority: [], strong: [], development: [], watch_list: [], poor: [] };
    visibleFits.forEach((f) => {
      const t = f.tier || "poor";
      if (groups[t]) groups[t].push(f);
    });
    return groups;
  }, [visibleFits]);

  const saveFit = async (form) => {
    if (!editor.fit) return;
    try {
      const r = await api.patch(`/team-fits/${editor.fit.id}`, form);
      setFits((arr) => arr.map((f) => f.id === r.data.id ? r.data : f));
      // Refresh summary
      const sm = await api.get("/team-fits/summary", { params: { player_id: playerId } });
      setSummary(sm.data);
      toast.success("Team fit saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  // Phase 7C: compare mode
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const toggleCompare = (fit) => {
    setCompareIds((arr) => {
      if (arr.includes(fit.id)) return arr.filter((x) => x !== fit.id);
      if (arr.length >= 4) { toast.error("You can compare up to 4 teams"); return arr; }
      return [...arr, fit.id];
    });
  };

  const openCompare = async () => {
    if (compareIds.length < 2) { toast.error("Select at least 2 teams to compare"); return; }
    try {
      const r = await api.get("/team-fits/compare", { params: { player_id: playerId, fit_ids: compareIds.join(",") } });
      setCompareData(r.data);
      setCompareOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Compare failed"); }
  };

  const exportComparePdf = async () => {
    if (compareIds.length < 2) { toast.error("Select at least 2 teams"); return; }
    setExporting(true);
    try {
      const { getStoredToken, API } = await import("@/lib/api");
      const token = getStoredToken();
      const res = await fetch(
        `${API}/team-fits/export.pdf?player_id=${encodeURIComponent(playerId)}&fit_ids=${encodeURIComponent(compareIds.join(","))}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OmniDraftLab_${(player?.full_name || "Player").replace(/\s+/g, "_")}_Team_Fit_Comparison.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); } finally { setExporting(false); }
  };

  const aiGenerate = async (fit) => {
    setAiBusy(true);
    try {
      const r = await api.post(`/team-fits/${fit.id}/ai-generate`);
      setFits((arr) => arr.map((f) => f.id === r.data.id ? r.data : f));
      setOpenFit(r.data);
      toast.success("AI draft ready for review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally { setAiBusy(false); }
  };

  const aiApprove = async (fit) => {
    setAiBusy(true);
    try {
      const r = await api.post(`/team-fits/${fit.id}/ai-approve`);
      setFits((arr) => arr.map((f) => f.id === r.data.id ? r.data : f));
      setOpenFit(r.data);
      toast.success("AI explanation published");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setAiBusy(false); }
  };

  const aiReject = async (fit) => {
    if (!window.confirm("Discard the AI draft for this team fit?")) return;
    setAiBusy(true);
    try {
      await api.post(`/team-fits/${fit.id}/ai-reject`);
      const cleared = { ...fit, ai_fit_explanation_draft: null };
      setFits((arr) => arr.map((f) => f.id === fit.id ? cleared : f));
      setOpenFit(cleared);
      toast.success("AI draft discarded");
    } catch (e) { toast.error("Failed"); } finally { setAiBusy(false); }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="max-w-[1500px] mx-auto">
      <PlayerSubNav />
      <PageHeader
        title="Team Fit Board"
        subtitle={
          isAdmin
            ? `Front-office decision board across ${summary?.total_teams || 32} NFL teams. Score, evaluate, and track meeting status.`
            : `Best NFL team fits for ${player?.full_name || "this player"} based on advisor analysis.`
        }
      />

      <div className="grid grid-cols-12 gap-5">
        <OverviewSummary summary={summary} />

        {/* Toolbar */}
        <GlassCard className="col-span-12 p-4" testId="tf-toolbar">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-3" />
              <Input
                data-testid="tf-search"
                placeholder="Search team…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-white/5 border-white/10 text-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-3" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="bg-white/5 border-white/10 text-1 min-w-[200px]" data-testid="tf-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10 max-h-72">
                  {FILTERS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-3.5 w-3.5 text-3" />
              <Select value={sortKey} onValueChange={setSortKey}>
                <SelectTrigger className="bg-white/5 border-white/10 text-1 min-w-[180px]" data-testid="tf-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  {SORT_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.value}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <StatusPill tone="ice" data-testid="tf-visible-count">{visibleFits.length} shown</StatusPill>
            {/* Phase 7C: compare mode */}
            <Button
              data-testid="tf-compare-toggle"
              variant="ghost"
              onClick={() => { setCompareMode((m) => !m); if (compareMode) setCompareIds([]); }}
              className={`text-2 hover:text-white ${compareMode ? "ring-1 ring-violet-300/40 bg-violet-300/10" : ""}`}
            >
              <Scale className="h-4 w-4" /> {compareMode ? "Exit compare" : "Compare teams"}
            </Button>
            {compareMode && (
              <>
                <StatusPill tone={compareIds.length >= 2 ? "violet" : "neutral"} data-testid="tf-compare-count">
                  {compareIds.length} selected
                </StatusPill>
                <Button
                  data-testid="tf-compare-open"
                  onClick={openCompare}
                  disabled={compareIds.length < 2}
                  className="cta-primary disabled:opacity-40"
                >
                  Open comparison
                </Button>
                {isAdmin && (
                  <Button
                    data-testid="tf-compare-export"
                    onClick={exportComparePdf}
                    disabled={compareIds.length < 2 || exporting}
                    variant="ghost"
                    className="text-2 hover:text-white disabled:opacity-40"
                  >
                    <FileDown className="h-4 w-4" /> {exporting ? "Exporting…" : "Export PDF"}
                  </Button>
                )}
              </>
            )}
          </div>
        </GlassCard>

        {/* Tier groups */}
        {Object.entries(byTier).map(([tierKey, items]) => {
          if (items.length === 0) return null;
          const meta = TIER_META[tierKey];
          return (
            <div key={tierKey} className="col-span-12" data-testid={`tf-tier-${tierKey}`}>
              <div className="flex items-center gap-3 mb-3 mt-1">
                <div className="h-7 w-7 rounded-md grid place-items-center text-[10px] font-mono font-bold" style={{ background: `${meta.color}25`, color: meta.color }}>{meta.short}</div>
                <div className="flex-1">
                  <div className="text-1 font-semibold">{meta.label}</div>
                  <div className="text-3 text-[11px]">{meta.description}</div>
                </div>
                <StatusPill tone={meta.tone}>{items.length} teams</StatusPill>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((f) => (
                  <TeamFitCard
                    key={f.id}
                    fit={f}
                    isAdmin={isAdmin}
                    onOpen={(x) => setOpenFit(x)}
                    onEdit={(x) => setEditor({ open: true, fit: x })}
                    compareMode={compareMode}
                    compareSelected={compareIds.includes(f.id)}
                    onToggleCompare={toggleCompare}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {visibleFits.length === 0 && (
          <GlassCard className="col-span-12 p-10 text-center" testId="tf-empty">
            <div className="text-2 text-sm">
              {isAdmin
                ? "No team fits match the current filter. Adjust filter or rate teams to populate the board."
                : "No published team fits yet. Your advisor will share recommendations soon."}
            </div>
          </GlassCard>
        )}
      </div>

      <TeamFitDrawer
        open={!!openFit}
        onOpenChange={(o) => { if (!o) setOpenFit(null); }}
        fit={openFit}
        isAdmin={isAdmin}
        positionFields={positionFields}
        onEdit={(f) => setEditor({ open: true, fit: f })}
        onAIGenerate={aiGenerate}
        onAIApprove={aiApprove}
        onAIReject={aiReject}
        aiBusy={aiBusy}
      />
      <TeamFitEditor
        open={editor.open}
        onOpenChange={(o) => setEditor((s) => ({ ...s, open: o, fit: o ? s.fit : null }))}
        fit={editor.fit}
        positionFields={positionFields}
        onSave={saveFit}
      />
      <CompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        data={compareData}
        isAdmin={isAdmin}
        onExportPdf={exportComparePdf}
        exporting={exporting}
      />
    </div>
  );
}

// ============================================================
// Phase 7C: 4-team Compare Dialog
// ============================================================
function CompareDialog({ open, onOpenChange, data, isAdmin, onExportPdf, exporting }) {
  const fits = data?.fits || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-6xl text-1 max-h-[90vh] overflow-y-auto" data-testid="tf-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-4 w-4 text-violet-300" /> Team Fit Comparison
          </DialogTitle>
        </DialogHeader>

        {fits.length === 0 ? (
          <div className="text-2 text-sm py-10 text-center">No fits to compare.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-separate border-spacing-0" data-testid="tf-compare-table">
              <thead>
                <tr>
                  <th className="text-left text-3 text-[10px] uppercase tracking-wider p-2 border-b border-white/10 bg-white/5 sticky left-0 z-10">Metric</th>
                  {fits.map((f) => (
                    <th key={f.id} className="p-3 border-b border-white/10 text-center bg-white/5 min-w-[160px]">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-9 w-9 rounded-lg bg-white/5 ring-1 ring-white/10 grid place-items-center font-mono font-bold text-xs">{f.team_abbreviation}</div>
                        <div className="text-left">
                          <div className="text-1 font-semibold text-sm leading-tight">{f.team_name}</div>
                          <StatusPill tone={(TIER_META[f.tier] || TIER_META.poor).tone}>{(TIER_META[f.tier] || TIER_META.poor).short}</StatusPill>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <CompareScoreRow label="Overall fit" fits={fits} getter={(f) => f.fit_score} big />
                {SUB_SCORES.map((s) => (
                  <CompareScoreRow key={s.key} label={s.label} fits={fits} getter={(f) => f[s.key]} color={s.color} />
                ))}
                <CompareTextRow label="Risk level" fits={fits} getter={(f) => (f.risk_level || "medium").toUpperCase()} />
                <CompareTextRow label="Draft range" fits={fits} getter={(f) => f.draft_range_estimate || "—"} />
                <CompareTextRow label="Meeting status" fits={fits} getter={(f) => (f.meeting_status || "no_contact").replace(/_/g, " ")} />
                <CompareTextRow label="High interest" fits={fits} getter={(f) => f.admin_marked_high_interest ? "Yes" : "No"} />
              </tbody>
            </table>

            {/* Per-team narratives */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
              {fits.map((f) => (
                <div key={f.id} className="glass p-3" data-testid={`tf-compare-narrative-${f.team_abbreviation}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-md bg-white/5 ring-1 ring-white/10 grid place-items-center font-mono font-bold text-xs">{f.team_abbreviation}</div>
                    <div className="text-1 font-semibold">{f.team_name}</div>
                  </div>
                  {f.ai_fit_explanation_md && f.ai_approved ? (
                    <div className="markdown-body markdown-compact text-2 text-[12.5px] leading-relaxed">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{f.ai_fit_explanation_md}</ReactMarkdown>
                    </div>
                  ) : (
                    <>
                      {f.scheme_reason && <div className="text-2 text-[12.5px] mb-1"><strong className="text-cyan-200">Why fits:</strong> {f.scheme_reason}</div>}
                      {f.needs_reason && <div className="text-2 text-[12.5px]"><strong className="text-rose-200">Why may not:</strong> {f.needs_reason}</div>}
                      {!f.scheme_reason && !f.needs_reason && <div className="text-3 text-[12px] italic">No narrative yet.</div>}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {isAdmin && (
            <Button data-testid="tf-compare-dialog-export" onClick={onExportPdf} disabled={exporting} variant="ghost" className="text-2 hover:text-white">
              <FileDown className="h-4 w-4" /> {exporting ? "Exporting…" : "Export PDF"}
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompareScoreRow({ label, fits, getter, color, big = false }) {
  const values = fits.map(getter).map((v) => Number(v) || 0);
  const max = Math.max(...values);
  return (
    <tr>
      <td className="text-3 text-[11px] uppercase tracking-wider p-2 border-b border-white/5 bg-white/5 sticky left-0 z-10">{label}</td>
      {fits.map((f, i) => {
        const v = values[i];
        const isBest = max > 0 && v === max;
        return (
          <td key={f.id} className="p-2 border-b border-white/5 text-center" data-testid={`tf-compare-cell-${f.team_abbreviation}-${label.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className={`inline-block font-mono ${big ? "text-2xl font-semibold" : "text-base"} ${isBest ? "text-emerald-300" : "text-1"}`}>
              {v || "—"}
            </div>
            {color && <div className="mt-1 h-1 w-14 mx-auto rounded bg-white/5 overflow-hidden"><div className="h-full rounded" style={{ width: `${v}%`, background: color }} /></div>}
          </td>
        );
      })}
    </tr>
  );
}

function CompareTextRow({ label, fits, getter }) {
  return (
    <tr>
      <td className="text-3 text-[11px] uppercase tracking-wider p-2 border-b border-white/5 bg-white/5 sticky left-0 z-10">{label}</td>
      {fits.map((f) => (
        <td key={f.id} className="p-2 border-b border-white/5 text-center text-2 text-[13px] capitalize">{getter(f)}</td>
      ))}
    </tr>
  );
}
